/*
 *	Copyright (c) 2011-2021 Acronis Incorporated. All rights reserved.
 *
 *	This source code may be used only by organizations licensed to use Acronis Access, in conjunction with normal Acronis Access usage.
 *	No portion of it may be redistributed or modified without the written permission of Acronis, Inc. See the Acronis Software License Agreement for more information.
 */

'use strict';
angular.module('access.Nodes')
    .constant('UPLOADER_EVENT', {
        QUEUE_UPDATED: 'uploader.queueUpdated',
        UPLOAD_START: 'uploader.uploadStart',
        UPLOAD_END: 'uploader.uploadEnd',
        ITEM_UPLOAD_START: 'uploader.itemUploadStart',
        ITEM_UPLOAD_UPDATE: 'uploader.itemUploadProgress',
        ITEM_UPLOAD_FAILED: 'uploader.itemUploadFailed',
        ITEM_UPLOAD_END: 'uploader.itemUploadEnd'
    })
    .factory('Uploader', [
        '$q',
        '$rootScope',
        'promiseChain',
        'UPLOADER_EVENT',
        'UPLOAD_ITEM_STATUS',
        'UploadFileItem',
        'UploadFolderItem',
        'UploadItem',
        'UploadStat',
        function ($q, $rootScope, promiseChain, UPLOADER_EVENT, UPLOAD_ITEM_STATUS, UploadFileItem, UploadFolderItem, UploadItem, UploadStat) {
            var BYTES_IN_MEGABYTE = 1048576;
            /**
             * Limit for the uploading queue. Mostly to keep uploading items list scroll smooth
             * 
             * @type {number}
             */
            var UPLOAD_QUEUE_LIMIT = 999;

            /**
             * @type {Array<(UploadFileItem|UploadFolderItem)>}
             */
            var uploadQueue = [];

            var emitter = new TinyEmitter();

            emitter.on(UPLOADER_EVENT.UPLOAD_START, function () {
                $rootScope.$broadcast('uploading', true);
            });

            emitter.on(UPLOADER_EVENT.UPLOAD_END, function () {
                $rootScope.$broadcast('uploading', false);

                if (angular.isObject($rootScope.account)) {
                    $rootScope.account.$get();
                }
            });

            /**
             * Return whether input file is a folder or not
             * 
             * @param {File} item - File to check
             * @returns {Promise}
             */
            function _detectFolder(item) {
                var deferred = $q.defer(),
                    reader = new FileReader();

                if (item.size > BYTES_IN_MEGABYTE && !item.type || item.size > (BYTES_IN_MEGABYTE * 5) && !!item.type) {
                    deferred.resolve(false);
                } else {
                    // Might be a folder, might be an unknown file, let's check
                    reader.onerror = function (e) {
                        deferred.resolve(true);
                    };
                    reader.onload = function (data) {
                        deferred.resolve(false);
                    };

                    // Attempt to access the first few bytes of the file, will throw an exception if it's a directory (caught in onerror)
                    reader.readAsText(item.slice(0, 5));
                }

                return deferred.promise;
            }

            /**
             * Walk through the file list and prepare them for uploading:
             * - if the folder was choosen, we'll group all files inside into a folder
             * - single file will be added to the list as is
             * 
             * @param {FileList} fileList 
             * @returns {Array.<(UploadFileItem|UploadFolderItem)>} Prepared list of items for uploading
             */
            function _getUploadItemsFromFileList(fileList) {
                return Array.prototype.reduce.call(
                    fileList,
                    /**
                     * 
                     * @param {Array.<UploadFileItem|UploadFolderItem>} uploadList - List of upladed elments
                     * @param {File} file - File is looking though
                     * @returns {Array.<UploadFileItem|UploadFolderItem>}
                     */
                    function (uploadList, file) {
                        var uploadFileItem = UploadFileItem.fromFile(file);

                        if (uploadFileItem.rootFolder) {
                            var folder = uploadList.filter(function (item) {
                                return item.isDirectory && item.name === uploadFileItem.rootFolder;
                            })[0];

                            if (folder) {
                                folder.addFile(uploadFileItem);
                            } else {
                                folder = new UploadFolderItem(uploadFileItem.rootFolder);
                                folder.addFile(uploadFileItem);
                                uploadList.push(folder);
                            }
                        } else {
                            uploadList.push(uploadFileItem);
                        }

                        return uploadList;
                    },
                    []
                );
            }

            /**
             * The dropped files has a little bit different API.
             * And the most operation with them are asynchronous.
             * That's why the current method returns Promise instead of result.
             * 
             * @param {DataTransfer} dataTransfer - DataTransfer input
             * @returns {Promise<Array.<(UploadFileItem|UploadFolderItem)>>} 
             */
            function _getUploadItemsFromDrop(dataTransfer) {
                /**
                 * @type {Promise<(UploadItem|null)>[]}
                 */
                var promisesToResolve = [];

                angular.forEach(
                    dataTransfer.items || dataTransfer.files,
                    /**
                     * 
                     * @param {DataTransferItem} item 
                     * @param {number} index 
                     */
                    function (item, index) {
                        var fileEntry = dataTransfer.files[index];

                        if (angular.isFunction(item.webkitGetAsEntry)) {
                            var webkitEntry = item.webkitGetAsEntry();

                            if (webkitEntry.isDirectory) {
                                promisesToResolve.push(
                                    UploadFolderItem.fromDrop(webkitEntry)
                                )
                            } else {
                                promisesToResolve.push(
                                    $q.resolve(UploadFileItem.fromDrop(webkitEntry, fileEntry))
                                );
                            }
                        } else {
                            /**
                             * If browser doesn't support reading folders
                             * we'll filter all folders from the queue
                             */
                            promisesToResolve.push(_detectFolder(fileEntry).then(function (isDirectory) {
                                if (!isDirectory) {
                                    return UploadFileItem.fromFile(fileEntry);
                                } else {
                                    return null;
                                }
                            }));
                        }
                    }
                );

                return $q.all(promisesToResolve).then(function (uploadItems) {
                    return uploadItems.filter(angular.isObject);
                });
            }


            /**
             * @callback
             * @this {(UploadFileItem|UploadFolderItem)}
             * @param {(UploadFileItem|UploadFolderItem)} item - Item which has been updated
             */
            function _onItemProgress(item) {
                emitter.emit(UPLOADER_EVENT.ITEM_UPLOAD_UPDATE, item);
            }

            return Object.defineProperties({}, {
                on: {
                    enumerable: false,
                    value: emitter.on.bind(emitter),
                    writable: false
                },
                off: {
                    enumerable: false,
                    value: emitter.off.bind(emitter),
                    writable: false
                },
                queue: {
                    enumerable: true,
                    get: function () {
                        return uploadQueue;
                    }
                },
                /**
                 * Prepare and add items to upload queue.
                 * Accept either FileList from <input type=file>,
                 * or DataTransfer instance of dropped files
                 * 
                 * @param {(FileList|DataTransfer)} input - Accept as Files
                 * @param {boolean} [onlyFiles=false] - Filter input items and leave only files
                 */
                addToQueue: {
                    value: function (input, onlyFiles) {
                        var inputIsFileList = input instanceof FileList || input instanceof File;
                        var inputIsDataTransfer = !inputIsFileList && input instanceof DataTransfer;
                        var newUploadItemsPromise = $q.resolve([]);

                        if (inputIsFileList) {
                            newUploadItemsPromise = $q.resolve(_getUploadItemsFromFileList(input));
                        } else if (inputIsDataTransfer) {
                            newUploadItemsPromise = _getUploadItemsFromDrop(input);
                        }

                        newUploadItemsPromise
                            .then(function (newUploadItems) {
                                if (onlyFiles === true) {
                                    return newUploadItems.filter(function (item) {
                                        return !item.isDirectory;
                                    });
                                }

                                return newUploadItems;
                            })
                            .then(function (newUploadItems) {
                                return newUploadItems.slice(0, UPLOAD_QUEUE_LIMIT - uploadQueue.length);
                            })
                            .then(function (newUploadItems) {
                                Array.prototype.push.apply(uploadQueue, newUploadItems);
                                emitter.emit(UPLOADER_EVENT.QUEUE_UPDATED);
                            });
                    }
                },
                splice: {
                    enumerable: false,
                    value: function (start, deleteCount) {
                        uploadQueue.splice(start, deleteCount);
                        emitter.emit(UPLOADER_EVENT.QUEUE_UPDATED);
                    }
                },
                isQueueFull: {
                    get: function () {
                        return uploadQueue.length >= UPLOAD_QUEUE_LIMIT;
                    }
                },
                /**
                 * Remove item from queue
                 * 
                 * @param {(UploadFileItem|UploadFileItem[])} item - Item or an array of items to remove
                 */
                removeFromQueue: {
                    value: function (item) {
                        if (!this.isUploading) {
                            var itemsForRemoval = !angular.isArray(item) ? [item] : item;

                            uploadQueue = uploadQueue.filter(function(queueItem) {
                                return itemsForRemoval.indexOf(queueItem) === -1;
                            });
                            
                            emitter.emit(UPLOADER_EVENT.QUEUE_UPDATED);
                        }
                    }
                },
                /**
                 * Remove all items from the queue
                 */
                clearQueue: {
                    value: function () {
                        uploadQueue = [];
                        emitter.emit(UPLOADER_EVENT.QUEUE_UPDATED);
                    }
                },
                /**
                 * Get newly added items: neither upload, nor canceled or failed
                 * 
                 * @type {Array.<(UploadFileItem|UploadFolderItem)>}
                 */
                newUploadItems: {
                    get: function () {
                        return uploadQueue.filter(function (uploadItem) {
                            return uploadItem.status === UPLOAD_ITEM_STATUS.NEW;
                        });
                    }
                },
                /**
                 * Upload currently added to the queue files
                 * 
                 * @param {Resource} folder - Target folder instance
                 * @param {Array.<(UploadFileItem|UploadFolderItem)>} [items=uploadQueue] - Items to upload
                 * @return {Promise}
                 */
                upload: {
                    value: function (folder, items) {
                        var that = this;
                        var queue = items && items.length > 0 ? items : that.newUploadItems;

                        emitter.emit(UPLOADER_EVENT.UPLOAD_START);

                        return promiseChain(queue.map(function (item) {
                            return function () {
                                emitter.emit(UPLOADER_EVENT.ITEM_UPLOAD_START, item);

                                return item.upload(folder, _onItemProgress.bind(null, item))
                                    .catch(function (error) {
                                        /**
                                         * We'll stop the process if we've lost connection with a server.
                                         * Otherwise, continue wih the rest of the items
                                         */
                                        if (error.status === 0 || error.status === -1) {
                                            return $q.reject(error);
                                        }
                                    })
                                    .finally(function () {
                                        emitter.emit(UPLOADER_EVENT.ITEM_UPLOAD_END, item);
                                    });
                            };
                        }))
                            .then(function () {
                                /**
                                 * If there are newly added items in the queue
                                 * while uploading, then process and upload'em
                                 */
                                var newUploadItems = that.newUploadItems;

                                if (newUploadItems.length > 0) {
                                    return that.upload(folder, newUploadItems);
                                }
                            })
                            .finally(function () {
                                emitter.emit(UPLOADER_EVENT.UPLOAD_END);
                            });
                    }
                },
                /**
                 * Is upload in progress
                 * 
                 * @type {boolean}
                 */
                isUploading: {
                    enumerable: true,
                    get: function () {
                        for (var i = 0, item = uploadQueue[i]; i < uploadQueue.length; item = uploadQueue[++i]) {
                            if (item.status === UPLOAD_ITEM_STATUS.UPLOADING) {
                                return true;
                            }
                        }

                        return false;
                    }
                },
                /**
                 * Cancel current uploads
                 */
                cancelUpload: {
                    value: function () {
                        for (var i = 0, item = uploadQueue[i]; i < uploadQueue.length; item = uploadQueue[++i]) {
                            if (item.isUploading) {
                                item.cancelUpload();
                                emitter.emit(UPLOADER_EVENT.UPLOAD_END);
                                break;
                            }
                        }
                    }
                },
                /**
                 * Overwrite and upload item
                 * 
                 * @param {Resource} folder - Target folder to upload item(s) to
                 * @param {(UploadFolderItem|UploadFileItem)} [item=undefined] - Item to overwrite, if ommited overwrite all conflict items
                 *                                                             and upload them
                 */
                overwriteAndUpload: {
                    value: function (folder, item) {
                        var conflictItems = [];

                        if (item.hasConflict) {
                            item.overwrite = true;
                            conflictItems.push(item);
                        } else {
                            uploadQueue.forEach(function (uploadItem) {
                                if (uploadItem.hasConflict) {
                                    uploadItem.overwrite = true;
                                    conflictItems.push(uploadItem);
                                }
                                return uploadItem;
                            });
                        }

                        if (conflictItems.length) {
                            this.upload(folder, conflictItems);
                        }
                    }
                },
                /**
                 * Return upload statistic
                 * 
                 * @returns {UploadStat} Statistics: amount of success, failed and canceled uploads
                 */
                stat: {
                    get: function () {
                        var stat = new UploadStat();

                        angular.forEach(uploadQueue, function (item) {
                            if (item.isDirectory) {
                                stat.add(item.stat);
                            } else {
                                stat[item.status]++;
                            }
                        });

                        return stat;
                    }
                }
            });
        }]);
